// ============================================================
// TMR Portal Admin — Empresa reads and writes, over @tmr/api-client
// ============================================================
import { useEffect, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { api } from '@tmr/api-client';
import type { Paged } from '@tmr/api-client';
import { companyKeys, contractKeys } from '../queryClient';
import { exportPageReader } from '../../lib/export/exportPageReader';
import type {
  BackfillBatch,
  ChangeCompanyStatusResponse,
  CompanyBusinessStatus,
  CompanyClassification,
  CompanyListItem,
  CompanyManagerOption,
  CompanyRecord,
  ExportStatus,
  ImportCompanyResponse,
  LegacyCompanySnapshot,
  OnboardCompanyRequest,
  OnboardCompanyResponse,
  OnboardingStatus,
  RecordOrigin,
  SubdomainSuggestion,
  UpdateCompanyRequest,
  UpdateCompanyResponse,
} from './contract';

/** Filters the Empresas list accepts; they are part of the cache key, so a change refetches. */
export interface CompanyListFilters {
  /** Free text matched by the server against name, CNPJ and subdomain. */
  search?: string;
  /** Business lifecycle filter — the screen's primary one; omitted lists every status. */
  businessStatus?: CompanyBusinessStatus;
  /** Export status filter; omitted lists every status. */
  status?: ExportStatus;
  /** Record origin filter; omitted lists both. Only meaningful together with `status: 'Exported'`. */
  origin?: RecordOrigin;
  /**
   * Gestor de Venda filter — a User id; omitted lists every manager's Empresas.
   *
   * A filter, never a scope: it narrows the page, not what the operator is allowed to see. "Minhas
   * empresas" is this field filled with the current operator's own id.
   */
  salesManagerUserId?: string;
  /** Gestor de Pós-venda filter, on the same terms. */
  postSalesManagerUserId?: string;
  /**
   * Registration completeness (19 §5): false lists only the records missing something — the usual case
   * after the legacy backfill — true only the complete ones, omitted both.
   */
  registrationComplete?: boolean;
  page?: number;
  pageSize?: number;
}

/** How many Empresas the table shows per page. */
export const COMPANY_PAGE_SIZE = 10;

/**
 * One page of Empresas, already scoped by the backend to what the operator may see (06 §6).
 *
 * @param filters Search, status and paging; every change is a new cache entry.
 */
export function useCompanyList(filters: CompanyListFilters = {}): UseQueryResult<Paged<CompanyListItem>> {
  const query = {
    search: filters.search,
    businessStatus: filters.businessStatus,
    status: filters.status,
    origin: filters.origin,
    salesManagerUserId: filters.salesManagerUserId,
    postSalesManagerUserId: filters.postSalesManagerUserId,
    registrationComplete: filters.registrationComplete,
    page: filters.page ?? 1,
    pageSize: filters.pageSize ?? COMPANY_PAGE_SIZE,
  };

  return useQuery({
    queryKey: companyKeys.list(query),
    queryFn: ({ signal }) => api.get<Paged<CompanyListItem>>('/companies', { query, signal }),
    // Keeping the previous page on screen while the next one loads avoids the table collapsing to a
    // skeleton on every keystroke of the search box.
    placeholderData: (previous) => previous,
  });
}

/**
 * Reads one page of the Empresas **export** (20 §3) — the same filters the table is using, so the file
 * matches the screen.
 *
 * It lives here rather than in the export kit because this is where the module's endpoints live: the kit
 * knows how to walk a paged route, not which route belongs to Empresas.
 *
 * @param filters The screen's active filters.
 */
export function companyExportPage(filters: CompanyListFilters = {}) {
  return exportPageReader<CompanyListItem>('/companies/export', {
    search: filters.search,
    businessStatus: filters.businessStatus,
    status: filters.status,
    origin: filters.origin,
    salesManagerUserId: filters.salesManagerUserId,
    postSalesManagerUserId: filters.postSalesManagerUserId,
    registrationComplete: filters.registrationComplete,
  });
}

/** How many Empresas a picker shows — enough to choose from, never a report. */
const COMPANY_PICKER_PAGE_SIZE = 8;

/** Below this many characters a search is not worth a round-trip. */
const MIN_SEARCH_LENGTH = 2;

/**
 * Empresas matching a term, for pickers that scope something to one of them (10 B7d).
 *
 * The same list endpoint as the table, asked in the shape a picker needs: a short page and just the
 * items. Gated by `company.read` on the server, so the caller hides the picker without it.
 *
 * @param term Free text matched by the server against name, CNPJ and subdomain.
 */
export function useCompanySearch(term: string): UseQueryResult<CompanyListItem[]> {
  const trimmed = term.trim();
  const query = { search: trimmed, page: 1, pageSize: COMPANY_PICKER_PAGE_SIZE };

  return useQuery({
    queryKey: companyKeys.list(query),
    queryFn: ({ signal }) => api.get<Paged<CompanyListItem>>('/companies', { query, signal }),
    enabled: trimmed.length >= MIN_SEARCH_LENGTH,
    select: (page) => page.items,
  });
}

/**
 * The colleagues an Empresa can be assigned to, for both manager pickers
 * (`GET /companies/managers`) — Gestor de Venda and Gestor de Pós-venda search the same directory.
 *
 * Its own endpoint rather than the operator directory, and that is the whole point: it is gated by
 * `company.onboard`/`company.update`, so filling in the registration no longer requires `users.manage` —
 * the permission that also creates operators, grants roles and disables accounts. It returns a name and
 * an e-mail and nothing else, and the server narrows it to the caller's scope.
 *
 * @param term Free text matched by the server against name and e-mail.
 */
export function useCompanyManagerSearch(term: string): UseQueryResult<CompanyManagerOption[]> {
  const trimmed = term.trim();
  const query = { search: trimmed, take: COMPANY_PICKER_PAGE_SIZE };

  return useQuery({
    queryKey: companyKeys.managers(query),
    queryFn: ({ signal }) =>
      api.get<CompanyManagerOption[]>('/companies/managers', { query, signal }),
    enabled: trimmed.length >= MIN_SEARCH_LENGTH,
  });
}

/** Where the carteira stands in its business lifecycle — the summary above the list. */
export interface CompanyCounts {
  total: number;
  pendingContract: number;
  awaitingSignature: number;
  completed: number;
}

/**
 * The Empresa counts per business status.
 *
 * They count the **business** lifecycle, not replication: how many Empresas are still waiting on a contract
 * says something about the carteira, whereas how many are exported says something about a background job.
 * The replication numbers are still reachable through the sync sub-filter, which reports its own total.
 *
 * Four count queries rather than one aggregate endpoint: each is a `COUNT` plus a single row, they are
 * cached like any other query, and inventing a summary route for a number the list already knows how to
 * produce would be backend work with no other consumer.
 *
 * They deliberately ignore the search box — this is the portfolio at a glance, not a description of the
 * filtered table, whose own total is shown next to the pagination.
 */
export function useCompanyCounts(): CompanyCounts {
  const total = useCountQuery();
  const pendingContract = useCountQuery('PendingContract');
  const awaitingSignature = useCountQuery('AwaitingSignature');
  const completed = useCountQuery('Completed');

  return { total, pendingContract, awaitingSignature, completed };
}

/** One count: the smallest page the server will serve, read for its `totalCount`. */
function useCountQuery(businessStatus?: CompanyBusinessStatus): number {
  const query = { page: 1, pageSize: 1, businessStatus };
  const result = useQuery({
    queryKey: companyKeys.list(query),
    queryFn: ({ signal }) => api.get<Paged<CompanyListItem>>('/companies', { query, signal }),
  });
  return result.data?.totalCount ?? 0;
}

/**
 * One Empresa's whole record.
 *
 * A 404 here means "unknown id **or** outside your scope" — the backend refuses to tell the two apart on
 * purpose, so the screen must not imply the Empresa exists (06 §6).
 *
 * **Refetches when the window regains focus** (`09` §11, N2). The Empresa's business status is moved by
 * things that happen off this screen — a contract signed at the provider, the export finishing — so
 * returning to the tab is when it is most likely to be stale. The global `staleTime` keeps a quick
 * alt-tab from costing a request.
 *
 * @param companyId The Empresa's id, or undefined while the route param is still resolving.
 */
export function useCompany(companyId: string | undefined): UseQueryResult<CompanyRecord> {
  return useQuery({
    queryKey: companyKeys.detail(companyId ?? ''),
    queryFn: ({ signal }) => api.get<CompanyRecord>(`/companies/${companyId}`, { signal }),
    enabled: Boolean(companyId),
    refetchOnWindowFocus: true,
  });
}

/**
 * The classifications an Empresa can be given (12 §3).
 *
 * Reference data owned by the legacy system, so it is fetched rather than declared here — which is the
 * whole point of the slice: the four values this screen used to offer (`Diamante`, `Ouro`, `Prata`,
 * `Bronze`) were invented in a mockup and matched nothing in the V1.
 *
 * Cached for a long time on purpose: five rows that change rarely, read by the wizard, the editor and the
 * detail. The backend caches too, so a stale minute here costs nothing and a refetch per screen would.
 */
export function useClassifications(): UseQueryResult<CompanyClassification[]> {
  return useQuery({
    queryKey: companyKeys.classifications,
    queryFn: ({ signal }) =>
      api.get<CompanyClassification[]>('/companies/classifications', { signal }),
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * How a classification reads in a list: the code and the band it stands for.
 *
 * @param classification The catalogue item, or the one stored on an Empresa.
 */
export function classificationLabel(classification: CompanyClassification): string {
  // A code with no band would leave the operator choosing between five letters; a band with no code loses
  // the word everyone actually uses. When the catalogue dropped the item, only the id is left to show.
  if (!classification.code) return `Classificação ${classification.id} (fora do catálogo)`;
  return classification.description
    ? `${classification.code} — ${classification.description}`
    : classification.code;
}

/** How often the export indicator asks again while the export is still Pending. */
const EXPORT_POLL_INTERVAL_MS = 3_000;

/**
 * The export status of an Empresa, polled while it is still running.
 *
 * The polling **stops** at a terminal state: an export that has succeeded or failed will not change
 * again on its own, and a timer that never ends is a tab that never idles.
 *
 * @param companyId The Empresa to watch.
 * @param enabled Pass false to stop asking entirely (e.g. the panel is closed).
 */
export function useOnboardingStatus(
  companyId: string | undefined,
  enabled = true,
): UseQueryResult<OnboardingStatus> {
  const queryClient = useQueryClient();
  const previousStatus = useRef<ExportStatus | undefined>(undefined);

  const query = useQuery({
    queryKey: companyKeys.onboardingStatus(companyId ?? ''),
    queryFn: ({ signal }) =>
      api.get<OnboardingStatus>(`/companies/${companyId}/onboarding-status`, { signal }),
    enabled: Boolean(companyId) && enabled,
    refetchInterval: (query) => (query.state.data?.status === 'Pending' ? EXPORT_POLL_INTERVAL_MS : false),
  });

  // Finishing the export is the one thing that changes an Empresa's record without anybody on this screen
  // asking for it, and until now the only thing that happened when it finished was that this poll stopped.
  // Everything else went on serving the snapshot taken while the Empresa was still Pending — including
  // `legacyProducerId`, which is null until the export succeeds.
  //
  // That was not a cosmetic staleness. The Contratos tab reads the Empresa from `companyKeys.detail`, and a
  // contract drawn up from that stale copy is written with `legacyPartyId: null` — the id the repasse bridge
  // needs. There is no `PATCH /contracts/{id}`, so the defect is permanent.
  //
  // It has to be invalidation rather than writing the fresher fields into the cached record: this response
  // carries a handful of the Empresa's fields, and merging them by hand would quietly define which parts of
  // the record are allowed to be right.
  const currentStatus = query.data?.status;
  useEffect(() => {
    if (previousStatus.current === 'Pending' && currentStatus && currentStatus !== 'Pending' && companyId) {
      // The whole resource, not just this Empresa's record: the lists carry `legacyProducerId` too, and the
      // picker on /contratos draws its Empresa from a list rather than from the detail.
      void queryClient.invalidateQueries({ queryKey: companyKeys.all });
    }
    previousStatus.current = currentStatus;
  }, [currentStatus, companyId, queryClient]);

  return query;
}

/**
 * A subdomain that is free right now, to pre-fill the field with.
 *
 * Only the server knows what is taken, so this cannot be computed in the browser — and it is a
 * suggestion, not a reservation: the value is only claimed when the Empresa is created.
 *
 * @param name The trade name the operator has typed so far.
 * @param enabled Pass false once the operator has edited the field themselves.
 */
export function useSubdomainSuggestion(name: string, enabled: boolean): UseQueryResult<SubdomainSuggestion> {
  const trimmed = name.trim();
  return useQuery({
    queryKey: ['company', 'subdomain-suggestion', trimmed] as const,
    queryFn: ({ signal }) =>
      api.get<SubdomainSuggestion>('/companies/subdomain-suggestion', { query: { name: trimmed }, signal }),
    enabled: enabled && trimmed.length > 1,
    // A name that produced a suggestion once produces the same one until something else takes it.
    staleTime: 60_000,
  });
}

/** Creates an Empresa. Answers 202: the record exists, the export to the legacy system has not run yet. */
export function useOnboardCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: OnboardCompanyRequest) =>
      api.post<OnboardCompanyResponse>('/companies/onboard', request),
    onSuccess: () => {
      // The new Empresa belongs on every list, whatever filter is showing.
      void queryClient.invalidateQueries({ queryKey: companyKeys.all });
    },
  });
}

/** Replaces an Empresa's registration. An edit the legacy has not seen leaves the export Pending. */
export function useUpdateCompany(companyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: UpdateCompanyRequest) =>
      api.put<UpdateCompanyResponse>(`/companies/${companyId}`, request),
    onSuccess: () => {
      // Invalidating the aggregate key refreshes the record and its export status together.
      void queryClient.invalidateQueries({ queryKey: companyKeys.detail(companyId) });
      void queryClient.invalidateQueries({ queryKey: companyKeys.all });
      // An edit that replaced the legal representative closes the pending contracts and draws up a new
      // one, in the background. Invalidating unconditionally is cheaper than working out whether it did:
      // the alternative is a Contratos tab that shows the old contract until the operator reloads, which
      // is exactly the kind of stale read that has already produced a bug here once.
      void queryClient.invalidateQueries({ queryKey: contractKeys.all });
    },
  });
}

/**
 * Moves an Empresa's business status (10 B6a). Gated by `company.update` on the server.
 *
 * Its own request, not part of the registration edit: moving an Empresa through its lifecycle is a different
 * act from correcting its cadastre, and the backend keeps them apart for the same reason. Any transition is
 * accepted — the contract-driven state machine arrives with M3.
 *
 * @param companyId The Empresa to move.
 */
export function useChangeCompanyStatus(companyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (status: CompanyBusinessStatus) =>
      api.patch<ChangeCompanyStatusResponse>(`/companies/${companyId}/status`, { status }),
    onSuccess: () => {
      // The record carries the status, and so does every list the Empresa appears on.
      void queryClient.invalidateQueries({ queryKey: companyKeys.detail(companyId) });
      void queryClient.invalidateQueries({ queryKey: companyKeys.all });
    },
  });
}

/** Re-enqueues an export that failed, after the operator fixed the cause. */
export function useRetryExport(companyId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => api.post<void>(`/companies/${companyId}/export/retry`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: companyKeys.detail(companyId) });
    },
  });
}

/**
 * Producers matching a term in the legacy system, so staff can pick which one to bring over.
 *
 * @param search Free text matched against the legacy name and CNPJ.
 */
export function useLegacySearch(search: string): UseQueryResult<LegacyCompanySnapshot[]> {
  const trimmed = search.trim();
  return useQuery({
    queryKey: ['company', 'legacy-search', trimmed] as const,
    queryFn: ({ signal }) =>
      api.get<LegacyCompanySnapshot[]>('/companies/legacy', { query: { search: trimmed }, signal }),
    enabled: trimmed.length > 2,
  });
}

/**
 * Brings **one batch** of the legacy producer base into tmr_v2 (19 §3).
 *
 * A mutation per batch rather than one call for the whole base: each producer is several round trips on
 * the server, so a request that did a thousand would time out and lose what it had already done. The walk
 * itself lives in the screen, which is also where the operator can stop it.
 *
 * The cache is invalidated per batch — the Empresas list is the thing being filled, and watching it grow
 * is how somebody knows it is working.
 */
export function useBackfillLegacyCompanies() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (after: number | null) =>
      api.post<BackfillBatch>(
        `/companies/import/legacy/backfill${after === null ? '' : `?after=${after}`}`,
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: companyKeys.all });
    },
  });
}

/** Brings one legacy producer into tmr_v2. Idempotent — importing twice returns the existing Empresa. */
export function useImportCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (legacyProducerId: number) =>
      api.post<ImportCompanyResponse>('/companies/import', { legacyProducerId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: companyKeys.all });
    },
  });
}
